import { createGuide, createStep } from '@/guides/helpers/guide-editor.util';
import { copyVocItemMedia } from '@/voc/api/media.api';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { BuildingBlock, BuildingBlockLayouts, Polls } from '@pendo/services/BuildingBlocks';
import { $can, $canSub } from '@pendo/permissions';
import moment from 'moment-timezone';
import { dateOneIsEarlierOrSame, normalizeDateTime } from '@/guides/helpers/common.util';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import VocItemsCountAggregation from '@/voc/aggregations/voc-items-count/voc-items-count.aggregation';

const { VALIDATE } = SUB_PRODUCT_AREAS || {};

const vocPollWizard = {
    namespaced: true,
    state: {
        details: {
            name: '',
            appId: null,
            theme: null,
            complete: false,
        },
        appHasIdeas: true,
        items: {
            title: '',
            description: '',
            vocItems: [],
            isSinglePageLayout: true,
            complete: false,
        },
        segment: {},
        scheduling: {
            showsAfter: '',
            showsAfterTime: '',
            expiresAfter: '',
            expiresAfterTime: '',
            hasExpiresAfter: false,
            hasMadeEditsToStartDateTime: false,
            hasMadeEditsToExpirationDateTime: false,
        },
        audience: [],
        audienceUiHint: [],
        currentStepIndex: 0,
        steps: ['Details', 'Content', 'Segment', 'Schedule'],
        guide: null,
        saveInProgress: false,
        savedStepsCheatSheet: {
            title: '',
            description: '',
            theme: null,
            vocItemIds: [],
            isSinglePageLayout: true,
        },
        wizardOpen: false,
    },
    mutations: {
        setDetails (state, details) {
            state.details = { ...state.details, ...details };
        },
        setItems (state, items) {
            state.items = items;
        },
        setSegment (state, { segment, audience, audienceUiHint }) {
            state.segment = segment;
            state.audience = audience;
            state.audienceUiHint = audienceUiHint;
        },
        setScheduling (state, scheduling) {
            state.scheduling = scheduling;
        },
        setCurrentStepIndex (state, index) {
            state.currentStepIndex = index;
        },
        setGuide (state, guide) {
            state.guide = guide;
        },
        setSaveInProgress (state, saveInProgress) {
            state.saveInProgress = saveInProgress;
        },
        setSavedStepsCheatSheet (state, cheatSheet) {
            state.savedStepsCheatSheet = cheatSheet;
        },
        setAppHasIdeas (state, appHasIdeas) {
            state.appHasIdeas = appHasIdeas;
        },
        setWizardOpen (state, { wizardOpen }) {
            state.wizardOpen = wizardOpen;
        },
    },
    actions: {
        async saveAppIdDetails ({ commit, state }, appId = null) {
            if (appId !== state.details.appId) {
                commit('setItems', {
                    title: '',
                    description: '',
                    vocItems: [],
                    isSinglePageLayout: true,
                    complete: false,
                });
                commit('setSavedStepsCheatSheet', {
                    title: '',
                    description: '',
                    theme: null,
                    isSinglePageLayout: true,
                    vocItemIds: [],
                });
                commit('setSegment', {
                    segment: {},
                    audience: [],
                    audienceUiHint: [],
                });
                commit('setScheduling', {
                    showsAfter: '',
                    showsAfterTime: '',
                    expiresAfter: '',
                    expiresAfterTime: '',
                    hasExpiresAfter: false,
                    hasMadeEditsToStartDateTime: false,
                    hasMadeEditsToExpirationDateTime: false,
                });
                commit('setSaveInProgress', true);
                try {
                    VocItemsCountAggregation.subProductArea = VALIDATE;
                    const vocItemCount = (await runAggregationOnce(VocItemsCountAggregation, { appId })).count;
                    commit('setSaveInProgress', false);
                    commit('setAppHasIdeas', vocItemCount > 0);
                } catch (err) {
                    commit('setSaveInProgress', false);
                    throw err;
                }
            }

            commit('setDetails', { appId });
        },
        setWizardOpen ({ commit }, wizardOpen) {
            commit('setWizardOpen', { wizardOpen });
        },
        saveDetailsNameTheme ({ commit }, { name = '', theme = null }) {
            commit('setDetails', { name, theme });
        },
        saveItems (
            { commit },
            { vocItems = [], title = '', description = '', isSinglePageLayout = true, formValidated = false },
        ) {
            const complete = vocItems.length > 0 && formValidated;
            commit('setItems', { title, description, vocItems, isSinglePageLayout, complete });
        },
        saveSegment ({ commit }, { segment = {}, audience = [], audienceUiHint = [] }) {
            commit('setSegment', { segment, audience, audienceUiHint });
        },
        saveScheduling (
            { commit },
            {
                showsAfter = '',
                showsAfterTime = '',
                expiresAfter = '',
                expiresAfterTime = '',
                hasExpiresAfter = false,
                hasMadeEditsToStartDateTime = false,
                hasMadeEditsToExpirationDateTime = false,
            },
        ) {
            commit('setScheduling', {
                showsAfter,
                showsAfterTime,
                expiresAfter,
                expiresAfterTime,
                hasExpiresAfter,
                hasMadeEditsToStartDateTime,
                hasMadeEditsToExpirationDateTime,
            });
        },
        reset ({ commit }) {
            commit('setDetails', { name: '', appId: null, theme: null, complete: false });
            commit('setItems', { title: '', description: '', vocItems: [], isSinglePageLayout: true, complete: false });
            commit('setCurrentStepIndex', 0);
            commit('setGuide', null);
            commit('setSavedStepsCheatSheet', {
                title: '',
                description: '',
                theme: null,
                vocItemIds: [],
                isSinglePageLayout: true,
            });
            commit('setSegment', { segment: {}, audience: [], audienceUiHint: [] });
            commit('setScheduling', {
                showsAfter: '',
                showsAfterTime: '',
                expiresAfter: '',
                expiresAfterTime: '',
                hasExpiresAfter: false,
                hasMadeEditsToStartDateTime: false,
                hasMadeEditsToExpirationDateTime: false,
            });
            commit('setAppHasIdeas', true);
        },
        async goForwardOneStep ({ commit, state, dispatch }) {
            await dispatch('saveGuide');

            if (state.currentStepIndex < state.steps.length - 1) {
                commit('setCurrentStepIndex', state.currentStepIndex + 1);
            }
        },
        goBackOneStep ({ commit, state }) {
            if (state.currentStepIndex > 0) {
                commit('setCurrentStepIndex', state.currentStepIndex - 1);
            }
        },
        async saveGuide ({ state, dispatch, commit, getters }) {
            try {
                commit('setSaveInProgress', true);
                if (state.currentStepIndex === 0) {
                    if (state.guide === null) {
                        await dispatch('initGuide');
                    } else if (state.guide.appId !== state.details.appId) {
                        await dispatch('deleteGuide');
                        await dispatch('initGuide');
                    } else if (state.guide.name !== state.details.name) {
                        await dispatch('updateGuideName');
                    }
                } else if (state.currentStepIndex === 1) {
                    if (
                        state.savedStepsCheatSheet.title !== state.items.title ||
                        state.savedStepsCheatSheet.description !== state.items.description ||
                        state.savedStepsCheatSheet.theme !== state.details.theme ||
                        state.savedStepsCheatSheet.isSinglePageLayout !== state.items.isSinglePageLayout ||
                        !isEqual(
                            state.savedStepsCheatSheet.vocItemIds,
                            state.items.vocItems.map((item) => item.id),
                        )
                    ) {
                        await dispatch('updateGuideSteps');
                    }
                } else if (state.currentStepIndex === 2) {
                    if (!isEmpty(state.segment) && (!state.guide.segment || state.guide.segment !== state.segment)) {
                        await dispatch('updateGuideSegment');
                    }
                } else if (state.currentStepIndex === 3) {
                    if (
                        (state.scheduling.hasMadeEditsToStartDateTime ||
                            state.scheduling.hasMadeEditsToExpirationDateTime) &&
                        !getters.schedulingHasErrors
                    ) {
                        await dispatch('updateGuideSchedule');
                    }
                }
                commit('setSaveInProgress', false);
            } catch (err) {
                commit('setSaveInProgress', false);
                throw err;
            }
        },
        async initGuide ({ commit, state, dispatch }) {
            const ideaGuide = await createGuide({
                guideType: 'building-block',
                app: {
                    id: state.details.appId,
                },
            });

            ideaGuide.attributes.type = 'building-block';

            ideaGuide.name = state.details.name;
            ideaGuide.steps = [];
            ideaGuide.polls = [
                {
                    attributes: {
                        type: 'Vote',
                    },
                },
            ];
            const createdGuide = await dispatch(
                'guides/saveGuide',
                {
                    guide: ideaGuide,
                },
                { root: true },
            );
            if (createdGuide) {
                commit('setGuide', createdGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
        },
        async deleteGuide ({ state, dispatch, commit }) {
            if (state.guide) {
                await dispatch(
                    'guides/remove',
                    {
                        guide: state.guide,
                    },
                    { root: true },
                );

                commit('setGuide', null);
            }
        },
        async updateGuideName ({ state, dispatch, commit }) {
            const tmpGuide = cloneDeep(state.guide);

            tmpGuide.name = state.details.name;
            const patchedGuide = await dispatch(
                'guides/patchGuide',
                {
                    guide: tmpGuide,
                    fieldsToPatch: ['name'],
                },
                { root: true },
            );

            if (patchedGuide) {
                commit('setGuide', patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
        },
        async updateGuideSegment ({ state, dispatch, commit }) {
            const tmpGuide = cloneDeep(state.guide);

            tmpGuide.segment = state.segment;
            tmpGuide.audience = state.audience;
            tmpGuide.audienceUiHint = state.audienceUiHint;
            const patchedGuide = await dispatch(
                'guides/patchGuide',
                {
                    guide: tmpGuide,
                    fieldsToPatch: ['segment', 'audience', 'audienceUiHint'],
                },
                { root: true },
            );

            if (patchedGuide) {
                commit('setGuide', patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test Segment');
            }
        },
        async updateGuideSchedule ({ state, dispatch, commit }) {
            const tmpGuide = cloneDeep(state.guide);
            const showsAfter =
                state.scheduling.showsAfter || state.scheduling.showsAfterTime
                    ? normalizeDateTime(state.scheduling.showsAfter, state.scheduling.showsAfterTime)
                    : undefined;
            const expiresAfter =
                state.scheduling.hasExpiresAfter && state.scheduling.hasMadeEditsToExpirationDateTime
                    ? normalizeDateTime(state.scheduling.expiresAfter, state.scheduling.expiresAfterTime)
                    : undefined;

            tmpGuide.showsAfter = showsAfter;
            tmpGuide.expiresAfter = expiresAfter;

            const patchedGuide = await dispatch(
                'guides/patchGuide',
                {
                    guide: tmpGuide,
                    fieldsToPatch: ['showsAfter', 'expiresAfter'],
                },
                { root: true },
            );

            if (patchedGuide) {
                commit('setGuide', patchedGuide);
            } else {
                throw new Error('An error occurred while saving the Idea Test Scheduling');
            }
        },
        async updateGuideSteps ({ state, dispatch, commit }) {
            /* eslint-disable no-await-in-loop */
            const tmpGuide = cloneDeep(state.guide);
            tmpGuide.polls = [];
            for (let i = 0; i < tmpGuide.steps.length; i++) {
                await dispatch(
                    'guides/removeGuideStep',
                    {
                        remoteGuide: tmpGuide,
                        step: tmpGuide.steps[i],
                    },
                    { root: true },
                );
            }

            const landingStep = await createStep({ guide: tmpGuide, type: 'building-block' });
            landingStep.attributes.themeId = state.details.theme.id;
            landingStep.content = null;

            const successStep = await createStep({ guide: tmpGuide, type: 'building-block' });
            successStep.attributes.themeId = state.details.theme.id;
            successStep.content = null;
            successStep.pollIds = [];

            const ideasBlock = [];
            const ideaSteps = [];
            const pollIdsSinglePage = [];
            for (let i = 0; i < state.items.vocItems.length; i++) {
                const votePollId = Math.random()
                    .toString(36)
                    .substring(2);
                const textPollId = Math.random()
                    .toString(36)
                    .substring(2);

                const ideaStep = await createStep({ guide: tmpGuide, type: 'building-block' });
                ideaStep.attributes.themeId = state.details.theme.id;
                ideaStep.content = null;
                ideaStep.pollIds = [votePollId, textPollId];

                let bannerImageUrl;
                if (state.items.vocItems[i].bannerImageId) {
                    const bannerImage = await copyVocItemMedia({
                        guideId: tmpGuide.id,
                        vocItemId: state.items.vocItems[i].id,
                        mediaFileType: 'bannerImage',
                        mediaFileId: state.items.vocItems[i].bannerImageId,
                    });
                    bannerImageUrl = bannerImage.data.url;
                }

                const ideaLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                    'ideasDetails',
                    tmpGuide.id,
                    {
                        vocItem: {
                            id: state.items.vocItems[i].id,
                            title: state.items.vocItems[i].vocItemTitle,
                            description: state.items.vocItems[i].vocItemDescription,
                            bannerImageUrl,
                        },
                        baseStepId: landingStep.id,
                        successStepId: successStep.id,
                        successStepIndex: state.items.vocItems.length + 2,
                        votePollId,
                        textPollId,
                        isSinglePageLayout: state.items.isSinglePageLayout,
                    },
                    state.details.theme,
                );
                ideaStep.buildingBlocks = JSON.stringify(ideaLayout);
                ideaStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(ideaLayout));
                ideaSteps.push(ideaStep);
                const pollIdRandNumber = Math.random()
                    .toString(36)
                    .substring(2);
                const pollIdSinglePage = `${state.items.vocItems[i].id}__${pollIdRandNumber}`;
                pollIdsSinglePage.push(pollIdSinglePage);
                ideasBlock.push({
                    vocItem: {
                        title: state.items.vocItems[i].vocItemTitle,
                        description: state.items.vocItems[i].vocItemDescription,
                        id: state.items.vocItems[i].id,
                    },
                    stepId: ideaStep.id,
                    pollId: pollIdSinglePage,
                });

                tmpGuide.polls.push(...Polls.generatePollsForBlocks(ideaLayout));
            }
            const landingPageLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                'ideas',
                tmpGuide.id,
                {
                    title: state.items.title,
                    subTitle: state.items.description,
                    ideasBlock,
                    isSinglePageLayout: state.items.isSinglePageLayout,
                    successStepId: successStep.id,
                    successStepIndex: state.items.vocItems.length + 2,
                },
                state.details.theme,
            );
            landingStep.pollIds = pollIdsSinglePage;
            landingStep.buildingBlocks = JSON.stringify(landingPageLayout);
            landingStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(landingPageLayout));
            tmpGuide.polls.push(...Polls.generatePollsForBlocks(landingPageLayout));
            const successMessageLayout = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
                'ideasSuccessMessage',
                tmpGuide.id,
                {
                    title: 'Thank you for your feedback',
                    subTitle: 'Building great products is hard and your feedback will help us grow!',
                    landingStepId: landingStep.id,
                    isSinglePageLayout: state.items.isSinglePageLayout,
                },
                state.details.theme,
            );
            successStep.buildingBlocks = JSON.stringify(successMessageLayout);
            successStep.dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(successMessageLayout));

            tmpGuide.steps = [landingStep, ...ideaSteps, successStep];
            for (let i = 0; i < tmpGuide.steps.length; i++) {
                const savedStep = await dispatch(
                    'guides/saveGuideStep',
                    {
                        remoteGuide: tmpGuide,
                        updatedStep: tmpGuide.steps[i],
                    },
                    { root: true },
                );
                tmpGuide.steps[i] = savedStep;
            }

            const patchedGuide = await dispatch(
                'guides/patchGuide',
                {
                    guide: tmpGuide,
                    fieldsToPatch: ['polls'],
                },
                { root: true },
            );
            if (patchedGuide) {
                commit('setGuide', patchedGuide);
                commit('setSavedStepsCheatSheet', {
                    title: state.items.title,
                    description: state.items.description,
                    theme: state.details.theme,
                    isSinglePageLayout: state.items.isSinglePageLayout,
                    vocItemIds: state.items.vocItems.map((item) => item.id),
                });
            } else {
                throw new Error('An error occurred while saving the Idea Test');
            }
        },
        /* eslint-enable no-await-in-loop */
    },
    getters: {
        details: (state) => state.details,
        detailsComplete: (state) => state.details.appId !== null && state.details.name !== '' && state.appHasIdeas,
        items: (state) => state.items,
        appHasIdeas: (state) => state.appHasIdeas,
        currentStepIndex: (state) => state.currentStepIndex,
        steps: (state) => state.steps,
        segment: (state) => state.segment,
        scheduling: (state) => state.scheduling,
        validStartDate: (state) => {
            if (!state.scheduling.hasMadeEditsToStartDateTime) return true;

            return moment(state.scheduling.showsAfter).isSameOrAfter(moment(), 'day');
        },
        validStartTime: (state) => {
            if (!state.scheduling.hasMadeEditsToStartDateTime) return true;

            return moment(normalizeDateTime(state.scheduling.showsAfter, state.scheduling.showsAfterTime)).isAfter(
                moment(),
            );
        },
        validExpirationDate: (state) => {
            if (!state.scheduling.hasExpiresAfter || !state.scheduling.expiresAfter) return true;
            if (!state.scheduling.hasMadeEditsToExpirationDateTime && !state.scheduling.hasMadeEditsToStartDateTime) {
                return true;
            }
            const sameOrAfter = state.scheduling.showsAfter ? moment(state.scheduling.showsAfter) : moment();

            return moment(state.scheduling.expiresAfter).isSameOrAfter(sameOrAfter, 'day');
        },
        validExpirationTime: (state, getters) => {
            if (!state.scheduling.hasExpiresAfter) return true;
            if (state.scheduling.expiresAfterTime && !state.scheduling.expiresAfter) return false;
            if (!getters.validExpirationDate) return false;
            if (!state.scheduling.hasMadeEditsToExpirationDateTime) return true;

            if (
                state.scheduling.hasExpiresAfter &&
                moment(state.scheduling.showsAfter).isSameOrAfter(moment(), 'day')
            ) {
                return !dateOneIsEarlierOrSame(
                    state.scheduling.expiresAfter,
                    state.scheduling.expiresAfterTime,
                    state.scheduling.showsAfter,
                    state.scheduling.showsAfterTime,
                );
            }

            return moment(normalizeDateTime(state.scheduling.expiresAfter, state.scheduling.expiresAfterTime)).isAfter(
                moment(),
            );
        },
        schedulingHasErrors: (_state, getters) => {
            return (
                !getters.validStartDate ||
                !getters.validStartTime ||
                !getters.validExpirationDate ||
                !getters.validExpirationTime
            );
        },
        guideUrl: (state) => {
            if (state.guide === null) {
                return null;
            }

            return `/listen/validate/ideatests/${state.guide.id}?view=settings`;
        },
        guide: (state) => state.guide,
        saveInProgress: (state) => state.saveInProgress,
        hasCreateEditAppPerm: (_state, getters) => getters.filteredAppList.length > 0,
        wizardOpen: (state) => state.wizardOpen,
        filteredAppList: (state, getters, rootState, rootGetters) => rootGetters['apps/onlyIntegratedApps'],
        canCreatePoll: () =>
            $canSub()
                .entity('ideaPoll')
                .action('create'),
        canCreateGuide: () => (appId) =>
            $can(appId)
                .entity('guide')
                .action('create')
                .source('layout')
                .target('*'),
        canEditGuide: () => (appId) =>
            $can(appId)
                .entity('guide')
                .action('edit')
                .source('draft')
                .target('draft'),
        canRemoveGuide: () => (appId) =>
            $can(appId)
                .entity('guide')
                .action('remove')
                .source('draft')
                .target('draft'),
        canCreateStep: () => (appId) =>
            $can(appId)
                .entity('guideStep')
                .action('create')
                .source('layout')
                .target('*'),
        isWizardEnabled: (_state, getters, rootState) => {
            return getters.canCreatePoll && rootState.vocItems.vocItemCount > 0;
        },
        isSinglePageLayout: (state) => state.items.isSinglePageLayout,
    },
};

export default vocPollWizard;
